import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import MightLike, { RECOMMENDATION_TYPES } from "../mightLike/MightLike";
import useRecommendations from "../mightLike/useRecommendations";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { PAGE_AREAS } from "../../constants";
import getDefaultCarouselSettings from "../../utils/getDefaultCarouselSettings";
import {
  Carousel,
  CarouselMode,
} from "@asosteam/asos-web-component-library-carousel";
import useGrid from "../grid/useGrid";
import Grid from "../grid/Grid";
import { buildProductTileAriaLabel } from "../productTile/buildProductTileAriaLabel";
import PriceThenTitle from "../productTile/PriceThenTitle";
import TitleThenPrice from "../productTile/TitleThenPrice";
import ProductTile from "../productTile/ProductTile";
import useDiscountPercentage from "../../hooks/useDiscountPercentage";
import { useViewport } from "../../context/viewport/ViewportContext";
import { useIsInStockContext } from "../../context/isInStock/IsInStockContext";
import getRecommendationsTitleAriaLabel from "../mightLike/getRecommendationsTitleAriaLabel";
import {
  setIntersectionObserver,
  unsetIntersectionObserver,
} from "../../utils/setIntersectionObserver";
import { fireMightLikeVisibleAnalytics } from "../../analytics/pageInteraction";
import {
  fireViewItemListMarketing,
  fireProductComponentImpression,
  fireSponsoredYmalLoadMarketing,
  fireSponsoredYmalInViewMarketing,
  fireSponsoredYmalClickMarketing,
} from "../../utils/marketingTracking/marketingTrackingEvents";
import { IMPRESSION_DWELL_MS } from "../../hooks/useProductComponentImpression";
import { debounce } from "../../utils/debounce";

const INTERSECTION_OPTIONS = {
  rootMargin: "0px 0px -350px 0px",
  threshold: 0,
};

const IMPRESSION_INTERSECTION_OPTIONS = { threshold: 1.0 };

const RESIZE_DEBOUNCE_MS = 150;

const getProductKey = (product) =>
  product.productId ?? product.id ?? product.url;
function groupTilesIntoRows(tileNodes, products) {
  const rowsByTop = new Map();

  tileNodes.forEach((node, index) => {
    if (!node || !products?.[index]) {
      return;
    }

    const top = Math.round(node.offsetTop);
    const row = rowsByTop.get(top);

    if (row) {
      row.indices.push(index);
    } else {
      rowsByTop.set(top, { firstNode: node, indices: [index] });
    }
  });

  return Array.from(rowsByTop.values()).map(
    ({ firstNode, indices }, rowIndex) => ({
      firstNode,
      row: rowIndex + 1,
      products: indices.map((index) => products[index]),
    }),
  );
}

function getProductTiles({
  mightLikeProducts,
  gridEnabled,
  showDiscountPercentage,
  getTranslation,
  hasCriteoAds,
  getTileRef,
  onSponsoredAdClick,
}) {
  const PriceAndTitle = gridEnabled ? PriceThenTitle : TitleThenPrice;

  return mightLikeProducts.map(
    (
      {
        url,
        productTitle,
        priceData,
        colourWayId,
        productId,
        imageUrl,
        advertisement,
      },
      index,
    ) => (
      <ProductTile
        key={url}
        url={url}
        title={productTitle}
        colourWayId={colourWayId}
        productId={productId}
        imageUrl={imageUrl}
        priceData={priceData}
        index={index}
        pageArea={PAGE_AREAS.MIGHT_LIKE}
        shouldShowTitle={true}
        productTileAriaLabel={buildProductTileAriaLabel({
          getTranslation,
          title: productTitle,
          priceData,
          showDiscountPercentage,
        })}
        advertisement={advertisement}
        showAdvertElements={hasCriteoAds}
        elementRef={getTileRef ? getTileRef(index) : null}
        onSponsoredAdClick={onSponsoredAdClick}
      >
        <PriceAndTitle
          priceData={priceData}
          title={productTitle}
          showAdvertElements={hasCriteoAds}
        />
      </ProductTile>
    ),
  );
}

function handleFireMightLikeVisibleAnalytics(intersectionData) {
  if (intersectionData.isIntersecting) {
    fireMightLikeVisibleAnalytics();
  }
}

function shouldNotRender({ gridEnabled, mightLikeProducts }) {
  return gridEnabled === null || !mightLikeProducts?.length;
}

function renderContent({
  gridEnabled,
  mightLikeProducts,
  showDiscountPercentage,
  getTranslation,
  viewportCategory,
  hasCriteoAds,
  getTileRef,
  onSponsoredAdClick,
}) {
  if (gridEnabled) {
    return (
      <Grid ariaDescribedby="mightLikeTitle" gridTestId="mightLikeGrid">
        {getProductTiles({
          mightLikeProducts,
          gridEnabled,
          showDiscountPercentage,
          getTranslation,
          hasCriteoAds,
          getTileRef,
          onSponsoredAdClick,
        })}
      </Grid>
    );
  }

  return (
    <Carousel
      {...getDefaultCarouselSettings({
        getTranslation,
        viewportCategory,
        CarouselMode,
      })}
      ariaDescribedby="mightLikeTitle"
      carouselTestId="mightLikeCarousel"
    >
      {getProductTiles({
        mightLikeProducts,
        gridEnabled,
        showDiscountPercentage,
        getTranslation,
        hasCriteoAds,
        getTileRef,
        onSponsoredAdClick,
      })}
    </Carousel>
  );
}

export default function YouMightAlsoLike({
  forceGrid = false,
  renderLoadingElement,
}) {
  const { product, getTranslation } = useRuntimeConstants();
  const {
    mightLikeProducts,
    mightLikeProductsWithAdverts,
    hasCriteoAds,
    apiFailed,
    recsRequestId,
  } = useRecommendations(
    {
      productId: product?.id,
    },
    true,
  );
  const productsToDisplay = mightLikeProductsWithAdverts ?? mightLikeProducts;

  const { isInStock } = useIsInStockContext();
  const { gridEnabled: defaultGridEnabled } = useGrid({ isInStock });
  const gridEnabled = forceGrid ? true : defaultGridEnabled;
  const { showDiscountPercentage } = useDiscountPercentage();
  const { viewportCategory } = useViewport();
  const observer = useRef(null);
  const element = useRef(null);
  const tileNodes = useRef([]);
  const lastTrackedProductsKey = useRef(null);
  const firedRowKeys = useRef(new Set());
  const [layoutVersion, setLayoutVersion] = useState(0);
  const lastTrackedSponsoredKey = useRef(null);
  const lastTrackedSponsoredInViewKey = useRef(null);
  const lastTrackedSponsoredClickKey = useRef(null);

  const shouldNotRenderRecommendations = shouldNotRender({
    gridEnabled,
    mightLikeProducts: productsToDisplay,
  });
  const marketingListName = isInStock ? "pdp_ymal" : "pdp_sml";
  const marketingProductsKey = `${marketingListName}:${(productsToDisplay || [])
    .map(getProductKey)
    .join(",")}`;
  const sponsoredAdKey = `${marketingListName}:${(productsToDisplay || [])
    .filter((product) => product.advertisement)
    .map(getProductKey)
    .join(",")}`;

  const getTileRef = (index) => (node) => {
    tileNodes.current[index] = node;
  };

  const trackingRef = useCallback((node) => {
    if (node) {
      element.current = node;
      observer.current = setIntersectionObserver({
        element: element.current,
        options: INTERSECTION_OPTIONS,
        handleIntersectionData: handleFireMightLikeVisibleAnalytics,
        observeIntersectionOnce: true,
      });
    }
  }, []);

  const handleSponsoredAdClick = useCallback(
    (clickedProductId) => {
      if (
        !hasCriteoAds ||
        lastTrackedSponsoredClickKey.current === sponsoredAdKey
      ) {
        return;
      }

      fireSponsoredYmalClickMarketing({
        products: productsToDisplay,
        listName: marketingListName,
        clickedProductId,
      });
      lastTrackedSponsoredClickKey.current = sponsoredAdKey;
    },
    [hasCriteoAds, sponsoredAdKey, productsToDisplay, marketingListName],
  );

  useEffect(() => {
    return () => {
      unsetIntersectionObserver(element.current, observer.current);
    };
  }, []);

  useEffect(() => {
    if (
      !hasCriteoAds ||
      !element.current ||
      lastTrackedSponsoredInViewKey.current === sponsoredAdKey
    ) {
      return;
    }

    const tileLinks = Array.from(
      element.current.querySelectorAll("div[role='group'] a[href]"),
    );
    const sponsoredTileElements = (productsToDisplay || [])
      .map((product, index) =>
        product.advertisement ? tileLinks[index] : null,
      )
      .filter(Boolean);
    const visibleSponsoredTiles = new Set();
    let adInViewTimer;

    if (!sponsoredTileElements.length) {
      return;
    }

    const handleAdInView = (entry) => {
      if (entry.isIntersecting) {
        visibleSponsoredTiles.add(entry.target);

        if (!adInViewTimer) {
          adInViewTimer = setTimeout(() => {
            if (lastTrackedSponsoredInViewKey.current === sponsoredAdKey) {
              return;
            }

            lastTrackedSponsoredInViewKey.current = sponsoredAdKey;
            fireSponsoredYmalInViewMarketing({
              products: productsToDisplay,
              listName: marketingListName,
            });
          }, 1000);
        }
      } else {
        visibleSponsoredTiles.delete(entry.target);
        if (visibleSponsoredTiles.size === 0) {
          clearTimeout(adInViewTimer);
          adInViewTimer = null;
        }
      }
    };

    const adInViewObserver = setIntersectionObserver({
      element: element.current,
      options: { threshold: 0.5 },
      handleIntersectionData: handleAdInView,
      debounceMs: 0,
      autoObserve: false,
    });

    sponsoredTileElements.forEach((tile) => {
      adInViewObserver?.observe(tile);
    });

    return () => {
      clearTimeout(adInViewTimer);
      adInViewTimer = null;
      visibleSponsoredTiles.clear();
      sponsoredTileElements.forEach((tile) => {
        adInViewObserver?.unobserve(tile);
      });
    };
  }, [hasCriteoAds, marketingListName, productsToDisplay, sponsoredAdKey]);

  useEffect(() => {
    if (shouldNotRenderRecommendations) {
      lastTrackedProductsKey.current = null;
      lastTrackedSponsoredKey.current = null;
      lastTrackedSponsoredInViewKey.current = null;
      lastTrackedSponsoredClickKey.current = null;
      return;
    }

    if (lastTrackedProductsKey.current !== marketingProductsKey) {
      fireViewItemListMarketing({
        products: productsToDisplay,
        listName: marketingListName,
        recsRequestId,
      });
      lastTrackedProductsKey.current = marketingProductsKey;
    }

    if (!apiFailed && lastTrackedSponsoredKey.current !== sponsoredAdKey) {
      fireSponsoredYmalLoadMarketing({
        products: productsToDisplay,
        listName: marketingListName,
      });
      lastTrackedSponsoredKey.current = sponsoredAdKey;
    }
  }, [
    shouldNotRenderRecommendations,
    marketingListName,
    marketingProductsKey,
    sponsoredAdKey,
    productsToDisplay,
    recsRequestId,
    apiFailed,
  ]);

  useEffect(() => {
    firedRowKeys.current = new Set();
  }, [marketingProductsKey]);

  useEffect(() => {
    if (shouldNotRenderRecommendations || !gridEnabled) {
      return undefined;
    }

    const handleResize = debounce(() => {
      setLayoutVersion((version) => version + 1);
    }, RESIZE_DEBOUNCE_MS);

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [shouldNotRenderRecommendations, gridEnabled]);

  useEffect(() => {
    if (shouldNotRenderRecommendations) {
      return undefined;
    }

    const rows = groupTilesIntoRows(tileNodes.current, productsToDisplay);
    const dwellTimers = new Map();

    const rowObservers = rows.map(
      ({ firstNode, row, products: rowProducts }) => {
        const rowObserver = setIntersectionObserver({
          element: firstNode,
          options: IMPRESSION_INTERSECTION_OPTIONS,
          debounceMs: 0,
          handleIntersectionData: (entry, helpers) => {
            if (entry.isIntersecting) {
              clearTimeout(dwellTimers.get(firstNode));
              const timer = setTimeout(() => {
                dwellTimers.delete(firstNode);
                const rowKey = rowProducts.map(getProductKey).join(",");
                if (!firedRowKeys.current.has(rowKey)) {
                  firedRowKeys.current.add(rowKey);
                  fireProductComponentImpression({
                    products: rowProducts,
                    itemListName: marketingListName,
                    row,
                  });
                }
                helpers.unobserve(firstNode);
              }, IMPRESSION_DWELL_MS);
              dwellTimers.set(firstNode, timer);
            } else {
              const timer = dwellTimers.get(firstNode);
              if (timer) {
                clearTimeout(timer);
                dwellTimers.delete(firstNode);
              }
            }
          },
        });

        return { firstNode, rowObserver };
      },
    );

    return () => {
      dwellTimers.forEach((timer) => clearTimeout(timer));
      dwellTimers.clear();
      rowObservers.forEach(({ firstNode, rowObserver }) => {
        unsetIntersectionObserver(firstNode, rowObserver);
      });
    };
  }, [
    shouldNotRenderRecommendations,
    marketingListName,
    marketingProductsKey,
    productsToDisplay,
    viewportCategory,
    layoutVersion,
  ]);

  if (shouldNotRenderRecommendations) {
    const isLoading = !productsToDisplay || gridEnabled === null;

    if (renderLoadingElement && !apiFailed && isLoading) {
      return renderLoadingElement();
    }
    return null;
  }

  return (
    <MightLike
      id={"mightLike"}
      title={
        isInStock
          ? getTranslation("pdp_carousel_recommendations_title")
          : getTranslation("pdp_carousel_recommendations_out_of_stock")
      }
      titleAriaLabel={getRecommendationsTitleAriaLabel({
        getTranslation,
        gridEnabled,
        isInStock,
      })}
      trackingRef={trackingRef}
      type={
        gridEnabled ? RECOMMENDATION_TYPES.GRID : RECOMMENDATION_TYPES.CAROUSEL
      }
    >
      {renderContent({
        gridEnabled,
        mightLikeProducts: productsToDisplay,
        showDiscountPercentage,
        getTranslation,
        viewportCategory,
        hasCriteoAds,
        getTileRef,
        onSponsoredAdClick: handleSponsoredAdClick,
      })}
    </MightLike>
  );
}

YouMightAlsoLike.propTypes = {
  forceGrid: PropTypes.bool,
  renderLoadingElement: PropTypes.func,
};
